/**
 * Tenant-facing PRESENTATION vocabulary for the billing shapes (#1883).
 *
 * The metering vocabulary itself — variant fields, their closed value
 * domains, unit codes — lives in `metering-spec.ts` / `metering.ts`. This
 * module is its DISPLAY companion: one bilingual label per closed member, so
 * that every surface rendering a tier or a unit (the ops-console dictionaries
 * and the tenant catalog's `billing` document alike) reads the same words
 * from the same constant. Before this file the console's zh/en dictionaries
 * were a second spelling of the vendor's billing-unit words, and the tenant
 * catalog had none at all.
 *
 * CO-LOCATION BY TYPE, not by file: `metering-spec.ts` is far over the
 * repo's file-size guidance and records its own split seams, so the labels
 * live in this sibling module — but every table below is an exhaustive
 * `Record` keyed by the value union it labels, so adding a variant field, a
 * video-input value or a unit code does not compile until its label is
 * written down here. That is the "next to the value definition" property in
 * the form the type system can enforce.
 *
 * OPEN-DOMAIN values (a `resolution` of `480p`, a `size-quality` segment)
 * deliberately have NO table: their canonical spelling is already the
 * operator-facing and vendor-facing word, so they self-label
 * ({@link meteringVariantValueLabel}). A table of open values would be a
 * registry nobody maintains, and a missing entry would be a blank cell on a
 * price list.
 */

import type { MeteringProfileKind, UnitUsageUnit } from './metering.ts';
import type { MeteringVariantField } from './metering-spec.ts';
import { VIDEO_INPUT_VARIANT_VALUES } from './metering-spec.ts';

/** One display label, in both languages every tenant surface ships. */
export interface BilingualLabel {
  readonly zh: string;
  readonly en: string;
}

/**
 * The axis label of each variant field. Exhaustive over
 * {@link MeteringVariantField}: a fifth field does not compile untranslated.
 * The wording is the ops-console's incumbent dictionary text, moved here so
 * the console and the tenant catalog cannot drift apart.
 */
export const METERING_VARIANT_FIELD_LABELS: Readonly<
  Record<MeteringVariantField, BilingualLabel>
> = {
  size: { zh: '尺寸', en: 'size' },
  quality: { zh: '质量', en: 'quality' },
  resolution: { zh: '分辨率', en: 'resolution' },
  has_video_input: { zh: '视频输入', en: 'video input' },
};

/**
 * The labels of `has_video_input`'s closed two-value domain, keyed by ROLE to
 * mirror {@link VIDEO_INPUT_VARIANT_VALUES} member for member. The English
 * side is the vendor's own billing-unit words (#1937), which is also where
 * the canonical value strings came from.
 */
export const VIDEO_INPUT_VARIANT_LABELS: Readonly<
  Record<keyof typeof VIDEO_INPUT_VARIANT_VALUES, BilingualLabel>
> = {
  absent: { zh: '无视频输入', en: 'No video input' },
  present: { zh: '含视频输入', en: 'Includes video input' },
};

/**
 * The unit-of-sale label per unit code. Exhaustive over
 * {@link UnitUsageUnit}: a new unit does not compile untranslated.
 */
export const UNIT_USAGE_UNIT_LABELS: Readonly<Record<UnitUsageUnit, BilingualLabel>> = {
  call: { zh: '次', en: 'call' },
  second: { zh: '秒', en: 'second' },
  character: { zh: '字符', en: 'character' },
  image: { zh: '图', en: 'image' },
};

/**
 * The SHAPE of a profile's token price card — how a token-lane sales form's
 * price list reads and is authored, per generation and per tier.
 *
 *   - `'chat_buckets'`: the incumbent card. Input, output and the cache
 *     buckets are independently priced dimensions; every chat profile sells
 *     this card and nothing about it changes here.
 *   - `'single_rate'`: the vendor's price card states ONE ¥/Mtok figure per
 *     tier — no cache counters exist in the upstream's terminal usage at all,
 *     and input and output tokens bill at the same rate. Measured on
 *     `ark_video_token_v1` (Ark video, #1937): each of the two video-input
 *     tiers is a single per-Mtok price.
 *
 * PRESENTATION AND AUTHORING ONLY — STORAGE AND RATING ARE UNTOUCHED. A
 * `single_rate` generation is still stored as the ordinary token row set with
 * the single price written into BOTH `input_per_mtok_usd` and
 * `output_per_mtok_usd` and the cache buckets at 0, which is byte-identical
 * to what operators were hand-fudging before this shape existed — so every
 * already-billed event and every ledger number is unchanged, and the rating
 * kernel needs no new arm. What the shape changes is the SURFACES: the ops
 * console asks for one price per tier instead of four hand-equalized cells,
 * and the tenant billing document presents one rate instead of a bucket card
 * whose cache rows are structurally zero.
 *
 * Exhaustive over {@link MeteringProfileKind} on purpose: declaring a new
 * sales form does not compile until its card shape is written down, exactly
 * like its capability row.
 */
export type TokenPriceCardShape = 'chat_buckets' | 'single_rate';

export const TOKEN_PRICE_CARD_SHAPES: Readonly<
  Record<MeteringProfileKind, TokenPriceCardShape>
> = {
  anthropic_messages_v2: 'chat_buckets',
  openai_responses_v2: 'chat_buckets',
  openai_images_v1: 'chat_buckets',
  gemini_generate_content_v1: 'chat_buckets',
  openai_chat_v1: 'chat_buckets',
  legacy_4_bucket: 'chat_buckets',
  // The measured Ark card (#1937): one ¥/Mtok per video-input tier, no cache
  // counters in the upstream's terminal usage.
  ark_video_token_v1: 'single_rate',
  // Same card as `ark_video_token_v1` — one ¥/Mtok per tier, no cache
  // counters — with more tiers (video-input × resolution). Presentation
  // only; rating still stores the single figure in both input and output.
  ark_video_token_tiered_v1: 'single_rate',
  // Unit-lane profiles price through `model_unit_pricing` and never render a
  // token card; the row exists because the Record is exhaustive, and the
  // default shape is the no-op answer for a card that is never drawn.
  duration_seconds_v1: 'chat_buckets',
  ark_video_seconds_v1: 'chat_buckets',
  ark_video_seconds_ref_v1: 'chat_buckets',
  grok_video_seconds_v1: 'chat_buckets',
  grok_image_unit_v1: 'chat_buckets',
  text_characters_v1: 'chat_buckets',
  flat_call_v1: 'chat_buckets',
  openai_images_unit_v1: 'chat_buckets',
};

/**
 * The card shape for a profile-kind STRING off a stored row. Unknown or
 * retired spellings land on `'chat_buckets'` — the incumbent card, which is
 * the fail-conservative answer: it may show structural zeros, it can never
 * hide a priced dimension.
 */
export function tokenPriceCardShapeForProfileKind(profileKind: string): TokenPriceCardShape {
  const byName: Partial<Record<string, TokenPriceCardShape>> = TOKEN_PRICE_CARD_SHAPES;
  return byName[profileKind] ?? 'chat_buckets';
}

/**
 * The display label of one variant VALUE on one axis.
 *
 * Closed-domain values resolve through their label table; every open-domain
 * value self-labels — the canonical spelling IS the display string, in both
 * languages. A closed-domain value outside its table (a key written past the
 * write face) also self-labels rather than erroring: the price row exists and
 * hiding it would misstate the price list.
 */
export function meteringVariantValueLabel(
  field: MeteringVariantField,
  value: string,
): BilingualLabel {
  if (field === 'has_video_input') {
    if (value === VIDEO_INPUT_VARIANT_VALUES.absent) return VIDEO_INPUT_VARIANT_LABELS.absent;
    if (value === VIDEO_INPUT_VARIANT_VALUES.present) return VIDEO_INPUT_VARIANT_LABELS.present;
  }
  return { zh: value, en: value };
}
